import { useId } from 'react';
import { Mail, RefreshIcon } from 'assets/icons';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import { invoiceSendMailText } from 'components/invoiceSendMail/invoiceSendMailText';
import useModal from 'components/modal/useModal';
import Tooltip from 'components/tooltips/Tooltips';
import {
  MAIL_MAX_INVOICE_NUMBERS,
  isSendableInvoiceStatus,
} from 'constants/invoiceSending';
import UseContext from 'hooks/useContext';
import useInvoiceExport, { EXPORT_MAX_INVOICES } from './useInvoiceExport';

// What the api retries at once; more is refused with 400 INVOICE_NUMBERS_INVALID.
export const RETRY_MAX_INVOICES = 500;

const ICON_COLOR = '#FFFFFF';

const DownloadIcon = ({ wh, color }) => (
  <svg
    width={wh}
    height={wh}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M1.33398 11.3333L1.33398 12.1667C1.33398 13.5474 2.45327 14.6667 3.83398 14.6667L12.1673 14.6667C13.548 14.6667 14.6673 13.5474 14.6673 12.1667L14.6673 11.3333M4.66732 7.99999L8.00065 11.3333M8.00065 11.3333L11.334 7.99999M8.00065 11.3333L8.00065 1.33333"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Separator = () => (
  <span aria-hidden="true" className="h-4 w-px bg-gray-600" />
);

const BarButton = ({ onClick, disabled, tooltip, children }) => {
  const tooltipId = useId();
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-describedby={tooltip ? tooltipId : undefined}
      className={`flex items-center gap-2 rounded px-2 py-1 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[transparent] ${tooltip ? 'disabled:pointer-events-none' : ''}`}
    >
      {children}
    </button>
  );
  if (!tooltip) return button;

  // Some browsers send no mouse event to a disabled button: the wrapper takes
  // the hover for the shared Tooltip, which only listens to the mouse.
  return (
    <Tooltip
      placement="top"
      text={
        <span id={tooltipId} role="tooltip">
          {tooltip}
        </span>
      }
    >
      <span className={disabled ? 'cursor-not-allowed' : undefined}>
        {button}
      </span>
    </Tooltip>
  );
};

/**
 * The bar at the bottom of the invoice list while invoices are ticked
 * (JBWEB-1273, mockup 1.3): how many, then « Réessayer », « Envoyer par mail »
 * and « Export CSV ».
 *
 * « Réessayer » retries only the ticked invoices in error, through `onRetry`
 * (JBWEB-1267). It is not shown when there is nothing to retry, while an import
 * of the month runs (`isImportRunning`), or beyond what the api retries at once.
 *
 * « Envoyer par mail » (JBWEB-1282) opens the mail modal with the ticked
 * numbers, only when every one of them is sendable by the status the list
 * last gave it (`statuses`, from useInvoiceSelection): a status unknown, or
 * forgotten after a retry, counts as not sendable until the list reads it
 * again.
 */
const InvoiceSelectionBar = ({
  period,
  numbers,
  errorNumbers,
  onRetry,
  isImportRunning = false,
  statuses,
}) => {
  const { language, setModalData } = UseContext();
  const { toggle: toggleModal } = useModal();
  const text = (key, fallback) =>
    language.getValue(`invoiceList.selection.${key}`, fallback);
  const mailText = invoiceSendMailText(language);
  const { exportInvoices, isExporting } = useInvoiceExport();

  const count = numbers.length;
  if (count === 0) return null;

  const canRetry =
    errorNumbers.length > 0 &&
    !isImportRunning &&
    errorNumbers.length <= RETRY_MAX_INVOICES;

  const isTooManyToExport = count > EXPORT_MAX_INVOICES;

  let mailBlockedBy;
  if (
    !numbers.every((number) => isSendableInvoiceStatus(statuses?.get(number)))
  ) {
    mailBlockedBy = mailText('entry.notSendable');
  } else if (count > MAIL_MAX_INVOICE_NUMBERS) {
    mailBlockedBy = fillText(mailText('entry.tooMany'), {
      max: MAIL_MAX_INVOICE_NUMBERS,
    });
  }
  const openMail = () => {
    if (mailBlockedBy) return;
    setModalData({ invoiceNumbers: numbers });
    toggleModal('invoice_send_mail');
  };

  return (
    <div
      role="toolbar"
      aria-label={text('barLabel', 'Actions sur la sélection')}
      className="absolute bottom-20 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 whitespace-nowrap rounded-lg bg-gray-900 px-4 py-2 text-sm text-white shadow-lg"
    >
      <span className="px-1 font-medium">
        {count === 1
          ? text('countOne', '1 facture sélectionnée')
          : fillText(text('countMany', '{count} factures sélectionnées'), {
              count,
            })}
      </span>
      {canRetry && (
        <>
          <Separator />
          <BarButton onClick={() => onRetry(errorNumbers)}>
            <RefreshIcon wh={16} color={ICON_COLOR} />
            {text('retry', 'Réessayer')}
          </BarButton>
        </>
      )}
      <Separator />
      <BarButton
        onClick={openMail}
        disabled={Boolean(mailBlockedBy)}
        tooltip={mailBlockedBy}
      >
        <Mail wh={16} color={ICON_COLOR} />
        {text('sendByMail', 'Envoyer par mail')}
      </BarButton>
      <Separator />
      <BarButton
        onClick={() => exportInvoices(period, numbers)}
        disabled={isExporting || isTooManyToExport}
        tooltip={
          isTooManyToExport
            ? fillText(
                text(
                  'exportTooMany',
                  'Exporter {max} factures au plus à la fois',
                ),
                { max: EXPORT_MAX_INVOICES },
              )
            : undefined
        }
      >
        <DownloadIcon wh={16} color={ICON_COLOR} />
        {text('exportCsv', 'Export CSV')}
      </BarButton>
    </div>
  );
};

export default InvoiceSelectionBar;
